import { ControlsGroup, loadWrapper } from "aic";
import { useHBC } from "components/hierarchybrowser/HierarchyBrowserContextProvider";
import { SET_NEW_REPORT_DETAILS } from "components/hierarchybrowser/interface";
import { newReportInputs } from "components/hierarchypage/newReportInputs";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { eHierarchy } from "functions/api/enums";
import { getReportSuffixes } from "functions/api/hierarchy/getReportSuffixes";
import { getReportTypes } from "functions/api/hierarchy/getReportTypes";
import { IRequestStatus } from "functions/api/interface";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

/**
 * Input field component for entering and validating a new report name during copy.
 */
export const CopyReportModalName = () => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const { state, dispatch } = useHBC();

  const [reportTypes, setReportTypes] = useState<string[]>([]);
  const [reportSuffixes, setReportSuffixes] = useState<string[]>([]);

  const [requestTypes, setRequestTypes] = useState<IRequestStatus<null>>({
    requesting: false,
    error: false,
  });
  const getReportTypesData = useEffectEvent(async () => {
    const response = await loadWrapper(
      getReportTypes,
      null,
      requestTypes,
      setRequestTypes,
      appSettings.backEndUrl,
    );
    if (response?.success) {
      setReportTypes(response.data!);
    }
  });
  const [requestSuffixes, setRequestSuffixes] = useState<IRequestStatus<null>>({
    requesting: false,
    error: false,
  });
  const getReportSuffixesData = useEffectEvent(async () => {
    const response = await loadWrapper(
      getReportSuffixes,
      null,
      requestSuffixes,
      setRequestSuffixes,
      appSettings.backEndUrl,
    );
    if (response?.success) {
      setReportSuffixes(response.data!);
    }
  });

  useEffect(() => {
    getReportTypesData();
    getReportSuffixesData();
  }, []);

  const currentHierarchy = hierarchyContext?.hierarchyNodes.find(
    (h) => h.id === state?.currentHierarchy,
  );

  const thisHierarchyNewReportInputs = newReportInputs(
    currentHierarchy?.children.map((r) => r.name.toLowerCase()) ?? [],
    reportTypes,
    reportSuffixes,
  );

  const [currentValues, setCurrentValues] = useState<Record<string, string>>(
    Object.fromEntries(
      thisHierarchyNewReportInputs
        .filter((inp) => !inp.computeValue)
        .map((inp) => [inp.inputName, inp.initialValue ?? ""]),
    ),
  );
  const [textValid, setTextValid] = useState<boolean[]>(
    thisHierarchyNewReportInputs.map((inp) => {
      const isHidden =
        typeof inp.hidden === "function" ? inp.hidden(currentValues) : (inp.hidden ?? false);
      return !inp.validCondition || isHidden;
    }),
  );

  /**
   * Effect to dispatch when names change
   */
  useEffect(() => {
    dispatch?.({
      operation: SET_NEW_REPORT_DETAILS,
      newReportName: (currentValues["Name"] ?? "").trim(),
      newReportDescription: (
        currentValues["Description"] ??
        // istanbul ignore next
        ""
      ).trim(),
      newReportType: (
        currentValues["ReportType"] ??
        // istanbul ignore next
        ""
      ).trim(),
      newReportValid:
        (
          currentValues["Name"] ??
          // istanbul ignore next
          ""
        ).trim() !== "" &&
        (
          currentValues["Description"] ??
          // istanbul ignore next
          ""
        ).trim() !== "" &&
        (
          currentValues["ReportType"] ??
          // istanbul ignore next
          ""
        ).trim() !== "" &&
        !textValid.some((v) => v === false),
    });
  }, [currentValues, dispatch, textValid]);

  return !state ? null : (
    <Form className="mt-3">
      {state.currentHierarchyType !== eHierarchy.StudyDirectory ? (
        <Row
          className="my-1"
          style={{ height: "55px" }}
        >
          <Col className="d-flex justify-content-center">Reports have to be saved to studies</Col>
        </Row>
      ) : (
        <ControlsGroup
          baseId={`copy-report-new-name-form`}
          currentValues={currentValues}
          inputs={newReportInputs(
            currentHierarchy?.children.map((r) => r.name.toLowerCase()) ??
              // istanbul ignore next
              [],
            reportTypes,
            reportSuffixes,
          )}
          setCurrentValues={(vals) => setCurrentValues(vals)}
          setTextValid={setTextValid}
          spellCheck={false}
        />
      )}
    </Form>
  );
};

CopyReportModalName.displayName = "CopyReportModalName";
